import { getAccessToken } from '../authentication/supabaseService';
import { RestaurantSectionType } from '../../types/RestaurantSectionType';

export interface BackendRestaurantSection {
  id: string;
  restaurant_id: string;
  name: string;
  restaurant_section_type: RestaurantSectionType;
  created_at: string;
  updated_at: string;
}

export interface FrontendRestaurantSection {
  id: string;
  restaurantId: string;
  name: string;
  restaurantSectionType: RestaurantSectionType;
  createdAt: string;
  updatedAt: string;
}

function mapBackendToFrontend(s: BackendRestaurantSection): FrontendRestaurantSection {
  return {
    id: s.id,
    restaurantId: s.restaurant_id,
    name: s.name,
    restaurantSectionType: s.restaurant_section_type,
    createdAt: s.created_at,
    updatedAt: s.updated_at,
  };
}

export async function createRestaurantSection(
  restaurantId: string,
  name: string,
  restaurantSectionType: RestaurantSectionType
): Promise<FrontendRestaurantSection> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantSections/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantId, name, restaurantSectionType }),
  });

  if (!res.ok) {
    let errorMessage = `Failed to create section: ${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }

  const backend: BackendRestaurantSection = await res.json();
  return mapBackendToFrontend(backend);
}

export async function fetchSectionsByRestaurant(
  restaurantId: string
): Promise<FrontendRestaurantSection[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantSections/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch sections: ${res.status} ${res.statusText}`);
  }

  const backendList: BackendRestaurantSection[] = await res.json();
  return backendList.map(mapBackendToFrontend);
}

export async function fetchAllSections(): Promise<FrontendRestaurantSection[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantSections/getAll', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch sections: ${res.status} ${res.statusText}`);
  }

  const backendList: BackendRestaurantSection[] = await res.json();
  return backendList.map(mapBackendToFrontend);
}

export async function fetchSectionsByRestaurantIds(
  restaurantIds: string[]
): Promise<FrontendRestaurantSection[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantSections/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantIds }),
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch sections: ${res.status} ${res.statusText}`);
  }

  const backendList: BackendRestaurantSection[] = await res.json();
  return backendList.map(mapBackendToFrontend);
}

export async function updateRestaurantSection(
  id: string,
  name: string,
  restaurantSectionType: RestaurantSectionType
): Promise<FrontendRestaurantSection> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantSections/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name, restaurantSectionType }),
  });

  if (!res.ok) {
    let errorMessage = `Failed to update section: ${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }

  const backend: BackendRestaurantSection = await res.json();
  return mapBackendToFrontend(backend);
}

export async function deleteRestaurantSection(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantSections/delete/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    let errorMessage = `Failed to delete section: ${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }
}
